<template>
  <!--
    readExcelData: function(rowNumber, cells, dataTypes, func) excel data 읽을 때 사용
      - rowNumber: 현재 row number
      - cells: 현재 row 의 cell data
      - dataTypes: tableDataType, 사용자가 임의로 정하는 타입
      - func: ExcelUploadNormalTemplate.vue 의 method
        - func.save(cells, error) excel data 저장
        - func.findFieldByFieldName(fieldName) fieldName 으로 field 찾기, useFieldCell 이 true 일때만 사용
        - func.toTel(string) string 을 전화번호 타입으로 변경

    setSaveFormData
  -->
  <normal-excel-upload
    ref="normalExcelUpload"
    :board="board"
    title="압력용기 엑셀 업로드"
    :useFieldCell="true"
    :downloadLink="downloadLink"
    tableTitle="등록 대상 압력용기 정보"
    :tableHeader="tableHeader"
    :tableDataType="tableDataType"
    :readExcelData="readExcelData"
    :setSaveFormData="setSaveFormData"
    @onExcelDataChange="onExcelDataChange"
    @onDuplicateRemove="onDuplicateRemove"
  >
    <template #colgroup>
      <col width="120px" v-for="index in tableHeader.length -1" :key="`colgroup_${index}`"/>
    </template>

    <!--
      index: column index
      data: column data
      dataType: 사용자가 임의로 정하는 타입
      funcSetEditExcelData: function(index, data, errorMSG) edit excel data 수정시 사용
        - errorMGS = "": 정상
        - errorMGS = "error message": 에러 메시지
        - errorMGS = undefined: 에러 메시지 변경안함
    -->
    <template v-slot:edit="{index, data, dataType, funcSetEditExcelData}">
      <fragment v-if="dataType === 'no'">{{ data }}</fragment>
      <fragment v-else-if="dataType === 'field'">
        <field-selector class="w-full text-left"
                        v-model="data.id"
                        @select="(field) => onSelectField(field, {index, data, funcSetEditExcelData})"/>
      </fragment>
      <fragment v-else-if="dataType === 'date'">
        <date-selector class="w-full text-left"
                       :value="data"
                       @input="(date) => onSelectDate(date, index, {index, data, funcSetEditExcelData})"/>
      </fragment>
      <fragment v-else-if="dataType === 'number'">
        <number-input class="w-full"
                      :value="data"
                      @input="(number) => funcSetEditExcelData(index, number, '')"/>
      </fragment>
      <fragment v-else>
        <ml-input class="w-full"
                  :value="data"
                  @input="(value) => funcSetEditExcelData(index, value, '')"/>
      </fragment>
    </template>
    <template v-slot:column="{index, data, dataType}">
      <p v-if="index === 1">{{ data.field_name || "-" }}</p>
      <p v-else-if="dataType === 'number'">{{ data || ""  | comma }}</p>
      <p v-else>{{ data || "-" }}</p>
    </template>
  </normal-excel-upload>
</template>

<script>
import NormalExcelUpload from "@/views/excel-upload/NormalExcelUpload.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import config from "/public/config";
import NumberInput from "@/components/NumberInput.vue";
import { checkExcelDataDuplicates } from "@/util/ExcelCheckUtil";

export default {
  components: {
    NumberInput,
    DateSelector,
    FieldSelector,
    NormalExcelUpload,
  },
  props: {
    board: Object,
  },
  data() {
    return {
      downloadLink: `${config.BASE_API_URL}/storage/app/media/%EB%8F%99%EA%B5%AD%EC%A0%9C%EA%B0%95_%EC%9C%A0%ED%95%B4%EC%9C%84%ED%97%98%EA%B8%B0%EA%B3%84%EA%B8%B0%EA%B5%AC_%EC%95%95%EB%A0%A5%EC%9A%A9%EA%B8%B0_%EB%8C%80%EB%9F%89%EB%93%B1%EB%A1%9D.xlsx`,
      tableDataType: ['no', 'field', 'string', 'number', 'radio', 'string', 'date', 'string', 'date', 'date', 'string'],
      tableHeader: ['No.', '사업장명', '구분', '내용적(㎥)', '안전검사', '자체 관리번호', '설치일', '합격번호', '최근 안전검사일', '차기 안전검사일', '제작업체'],
      // 실제 form.subInfo 에 저장되는 데이터이므로 no 를 사용하지 않습니다.
      tableDataValue: ['구분', '내용적', '안전검사', '자체관리번호', '설치일', '합격번호', '최근안전검사일', '차기안전검사일', '제작업체'],
      // 중복 검사 관련 속성 추가
      existDataList: ['구분', '내용적', '자체관리번호'],
      isProcessing: false,
      lastProcessedData: null,
      debounceTimer: null,
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    boardId() {
      return parseInt(this.$route.params.boardId);
    },
  },
  methods: {
    onSelectField(field, edit) {
      const data = {
        id: field.id,
        field_name: field.field_name,
      }
      const errorMSG = field.id ? "" : "입력된 데이터가 검색되지 않습니다.";
      edit.funcSetEditExcelData(edit.index, data, errorMSG)
    },
    onSelectDate(date, index, edit) {
      if (date) {
        const data = new Date(date).format("yyyy-MM-dd");
        const errorMSG = !isNaN(Date.parse(date)) ? "" : "날짜 형식이 아닙니다.";
        edit.funcSetEditExcelData(edit.index, data, errorMSG)
      } else {
        edit.funcSetEditExcelData(edit.index, "", "");
      }
    },
    readExcelData(rowNumber, cells, dataTypes, func) {
      if (rowNumber > 2) {
        const field = func.findFieldByFieldName(cells[1]);
        const error = [];

        cells.forEach((cell, index) => {
          if (dataTypes[index] === "no") {
            cells[index] = func.rowIndex;
          } else if (dataTypes[index] === "field") {
            if (field) {
              cells[index] = {
                id: field.id,
                field_name: field.field_name,
              };
            } else {
              cells[index] = {
                id: 0,
                field_name: cells[index],
              };
              error[index] = "입력된 데이터가 검색되지 않습니다.";
            }
          } else if (dataTypes[index] === "date") {

            if (cells[index]) {
              if (!isNaN(Date.parse(cells[index]))) {
                cells[index] = new Date(cells[index]).format("yyyy-MM-dd");
              } else {
                error[index] = "날짜 형식이 아닙니다.";
              }
            }
          }
        });
        func.save(cells, error)
      }
    },

    setSaveFormData(datas, stdInfo) {
      const form = {
        stdInfo: {
          ...stdInfo,
          field_id: datas[1].id,
          field_name: datas[1].field_name,
        },
        subInfo: {},
      }
      this.tableDataValue.forEach((key, index) => {
        form.subInfo[key] = datas[index + 2];
      });

      const date = new Date();
      const header = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        date: date.format("yyyy-MM-dd"),
        full: date.format("yyyy-MM-dd")
      };

      return {
        header,
        body: form
      }
    },

    onExcelDataChange(datas) {
      // 이미 처리 중인 경우 중첩 호출 방지
      if (this.isProcessing) {
        return;
      }

      // 디바운스 처리 - 연속된 데이터 변경 시 마지막 변경만 처리
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.processExcelDataChanges(datas);
      }, 300); // 300ms 디바운스
    },

    processExcelDataChanges(datas) {
      this.isProcessing = true;

      try {
        // 중복 체크 전에 중복 관련 에러 메시지 초기화
        if (datas && datas.length > 0) {
          datas.forEach(item => {
            if (item.errorMSG && item.errorMSG[1] && item.errorMSG[1].includes('이미 등록된')) {
              // 중복 관련 에러 메시지 초기화
              item.errorMSG[1] = '';
            }
          });
        }

        // 유틸리티 함수 호출
        checkExcelDataDuplicates({
          excelData: datas,
          existDataList: this.existDataList,
          tableDataValue: this.tableDataValue,
          projectId: this.projectId,
          boardId: this.boardId,
          dispatch: this.$store.dispatch,
          lastProcessedData: this.lastProcessedData
        }).then(() => {
          // 마지막으로 처리한 데이터 업데이트
          this.lastProcessedData = JSON.parse(JSON.stringify(datas));

          // 중복 검사 결과 갱신
          if (this.$refs.normalExcelUpload && typeof this.$refs.normalExcelUpload.updateExcelData === 'function') {
            this.$refs.normalExcelUpload.updateExcelData(datas);
          }

          // UI 강제 업데이트
          this.$nextTick(() => {
            if (this.$refs.normalExcelUpload) {
              this.$refs.normalExcelUpload.$forceUpdate();
            }
          });
        });
      } catch (error) {
        console.error('중복 데이터 검사 중 오류 발생:', error);
      } finally {
        // 처리 완료 후 플래그 해제
        this.isProcessing = false;
      }
    },

    // 중복 데이터가 삭제되었을 때 호출되는 이벤트 핸들러
    onDuplicateRemove(datas) {
      // 데이터가 변경되었으므로 lastProcessedData 업데이트
      this.lastProcessedData = JSON.parse(JSON.stringify(datas));

      // 중복 검사를 다시 실행하지 않도록 플래그 설정
      this.isProcessing = true;

      // 데이터가 삭제된 후 UI 업데이트
      this.$nextTick(() => {
        if (this.$refs.normalExcelUpload) {
          this.$refs.normalExcelUpload.$forceUpdate();
        }

        // 처리 완료 후 플래그 해제 (약간의 딜레이를 두어 onExcelDataChange와 충돌 방지)
        setTimeout(() => {
          this.isProcessing = false;
        }, 100);
      });
    },
  },
};
</script>

<style scoped lang="scss">

</style>
